import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

interface SponsorshipThankYouModalProps {
  open: boolean;
  onClose: () => void;
  referenceNumber?: string;
  eventTitle?: string;
  primaryColor?: string;
}

const SponsorshipThankYouModal: React.FC<SponsorshipThankYouModalProps> = ({
  open,
  onClose,
  referenceNumber,
  eventTitle,
  primaryColor = "#1976d2",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const iconSize = isMobile ? 48 : 60;
  const titleSize = isMobile ? 20 : 24;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          margin: isMobile ? 1 : 2,
          maxHeight: isMobile ? "95vh" : "90vh",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          bgcolor: primaryColor,
          color: "white",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: isMobile ? 6 : 8,
            top: isMobile ? 6 : 8,
            color: "white",
            padding: "4px",
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: isMobile ? 1.5 : 2,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: iconSize, color: "white" }} />
        </Box>

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 1, fontSize: titleSize }}
        >
          Thank You!
        </Typography>
        <Typography
          variant={isMobile ? "subtitle1" : "body1"}
          fontWeight="bold"
          sx={{
            fontSize: "1.125rem",
            lineHeight: 1.4,
            color: "white",
          }}
        >
          Your sponsorship application has been submitted successfully!
        </Typography>

        {referenceNumber && (
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              fontSize: "0.9375rem",
              lineHeight: 1.4,
              maxWidth: "480px",
              mx: "auto",
              mt: 1,
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Your Reference Number is: <strong>{referenceNumber}</strong>
          </Typography>
        )}
      </Box>

      <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box
          sx={{
            bgcolor: "grey.50",
            borderRadius: 2,
            p: isMobile ? 2 : 2.5,
            mb: isMobile ? 2 : 3,
            textAlign: "center",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          {eventTitle && (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mb: 1, color: "text.primary" }}
            >
              Event: {eventTitle}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.5,
              fontSize: isMobile ? "0.8125rem" : undefined,
            }}
          >
            We appreciate your interest in sponsoring with us. Our team will
            review your application and contact you shortly via WhatsApp with
            more details about the sponsorship opportunity.
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            py: 1.5,
            borderRadius: 2,
            background: primaryColor,
            color: "white",
            fontWeight: 600,
            "&:hover": {
              background: primaryColor,
              opacity: 0.9,
            },
          }}
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SponsorshipThankYouModal;
